import { DateTime } from 'luxon'
import React from 'react'
import DatePicker from 'react-datepicker'

import { noOp } from '@/utils/helpers'
import logger from '@/utils/logger'

import { InputText } from '../styled'
import { FormContext, type FormInputType } from '../types'

const FormInputDateBox: React.FC<{
  value: string
  name: string
  placeholder: string
  onClick?: () => void
  onChange?: () => void
  disabled?: boolean
}> = React.forwardRef(
  (
    { value, name, placeholder, onClick, onChange, disabled = false },
    ref: React.ForwardedRef<HTMLInputElement>
  ) => (
    <InputText
      ref={ref}
      name={name}
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      onClick={onClick}
      onChange={onChange}
    />
  )
)

// TODO: Validate that input.initialValue includes tz information, otherwise
// this component assumes date is in UTC which can lead to unexpected behavior.
const FormInputDate: React.FC<{
  input: FormInputType & { type: 'date' }
}> = ({ input }) => {
  const { name: inputName, onChange: inputOnChange } = input
  const { submitting, onChange } = React.useContext(FormContext)
  const [stylesLoaded, setStylesLoaded] = React.useState(false)

  const [value, setValue] = React.useState<string | null>(input.initialValue ?? null)

  const handleChange = React.useCallback(
    (date: Date) => {
      const dateISO = date != null ? DateTime.fromJSDate(date).startOf('day').toISO() : null
      setValue(dateISO)
      onChange(inputName, dateISO)
      inputOnChange?.(dateISO)
    },
    [inputName, inputOnChange, onChange]
  )

  React.useEffect(() => {
    import('react-datepicker/dist/react-datepicker.css')
      .catch(err => {
        logger.error('Error loading styles for date picker', { err })
      })
      .finally(() => {
        setStylesLoaded(true)
      })
  }, [])

  // NOTE: These props are overriden by DatePicker
  // or used as palceholder until date picker is loaded
  const customInput = (
    <FormInputDateBox
      name="__date_placeholder"
      placeholder=""
      onChange={noOp}
      value={value != null ? new Date(value).toLocaleDateString() : ''}
      disabled={submitting || input.disabled}
    />
  )

  return stylesLoaded ? (
    <DatePicker
      disabled={submitting || input.disabled}
      name={input.name}
      minDate={input.minDate != null ? new Date(input.minDate) : null}
      maxDate={input.maxDate != null ? new Date(input.maxDate) : null}
      selected={value != null ? new Date(value) : null}
      onChange={handleChange}
      customInput={customInput}
    />
  ) : (
    customInput
  )
}

export default FormInputDate
